import React, { useEffect, useRef, useState } from 'react'
import TextInput from '../text-input'
import { Popper } from 'react-popper'
import DatePickerOptions from './datepicker-options'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import DatePickerCalendar from './datepicker-calendar'
import { getLocaleDateString } from '../helpers'
import OutsideClickHandler from 'react-outside-click-handler'
import { Range, PickerDate, PickerValue, DatePickerOptionValues } from './types'

const DatePicker = ({
  handleDateChange,
  defaultValue,
  defaultOption,
  children,
}: {
  handleDateChange: (range: Range<PickerDate>, option: DatePickerOptionValues) => any
  defaultValue?: Range<PickerDate>
  defaultOption?: DatePickerOptionValues
  children?: (label: string, isOpen: boolean) => React.ReactNode
}) => {
  const [isOpen, setOpen] = useState(false)
  const [isExpanded, setExpanded] = useState(false)
  const [selectedOption, setSelectedOption] = useState<DatePickerOptionValues>(DatePickerOptionValues.All_time)
  const [selectedRange, setSelectedRange] = useState<Range<PickerDate>>([null, null])
  const [calendarOption, setCalendarOption] = useState<DatePickerOptionValues>(DatePickerOptionValues.All_time)
  const [calendarRange, setCalendarRange] = useState<Range<PickerDate>>([null, null])

  const { _t } = useTypedTranslation()
  const referenceElement = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (defaultValue) {
      const date_from: Date | null = defaultValue[0] ? new Date(defaultValue[0]) : null
      const date_to: Date | null = defaultValue[1] ? new Date(defaultValue[1]) : null

      if (date_from) {
        const option = getSelectedDateOption(date_from, date_to)
        setSelectedOption(option)
        setSelectedRange([date_from, date_to])
        setCalendarOption(option)
        setCalendarRange([date_from, date_to])
      }
    } else if (defaultOption && defaultOption !== DatePickerOptionValues.All_time) {
      const dates = getDateFromOption(defaultOption)

      setSelectedOption(defaultOption)
      setSelectedRange(dates)
      setCalendarOption(defaultOption)
      setCalendarRange(dates)
      handleDateChange(dates, defaultOption)
    } else {
      setSelectedOption(DatePickerOptionValues.All_time)
      setSelectedRange([null, null])
      setCalendarOption(DatePickerOptionValues.All_time)
      setCalendarRange([null, null])
    }
  }, [defaultValue, defaultOption])

  const handleSimpleOptionSelect = (e: React.MouseEvent<HTMLElement>) => {
    const option = DatePickerOptionValues[e.currentTarget.id as keyof typeof DatePickerOptionValues]

    if (option === DatePickerOptionValues.Custom_range) {
      return setExpanded(true)
    }

    const rangeFromOption = getDateFromOption(option)

    setSelectedOption(option)
    setCalendarOption(option)
    setSelectedRange(rangeFromOption)
    setCalendarRange(rangeFromOption)
    handleDateChange(rangeFromOption, option)
    handleClose()
  }

  const handleClose = () => {
    setOpen(false)
    setExpanded(false)
  }

  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    const typedTarget = e.target as HTMLElement
    if (!isOpen) {
      setOpen(true)
    } else if (isOpen && (typedTarget.id === 'datepicker-input' || typedTarget.id === 'calendar-icon')) {
      handleClose()
    }
  }

  const calculateNewStartRange = (selectedDate: Date, prevRange: Range<PickerDate>): Range<PickerDate> => {
    // Check if at least one date in date range is null or selected start date is greater than end date
    const hasNullValue = prevRange.some((v) => !v)
    const isAfterEndDate = prevRange[1] ? selectedDate.getTime() > prevRange[1].getTime() : false

    const endDate = hasNullValue || isAfterEndDate ? selectedDate : prevRange[1]

    return [selectedDate, endDate]
  }

  const calculateNewEndRange = (selectedDate: Date, prevRange: Range<PickerDate>): Range<PickerDate> => {
    // Check if at least one date in date range is null or selected end date is less than end date
    const hasNullValue = prevRange.some((v) => !v)
    const isBeforeStartDate = prevRange[0] ? selectedDate.getTime() < prevRange[0].getTime() : false

    const startDate = hasNullValue || isBeforeStartDate ? selectedDate : prevRange[0]

    return [startDate, selectedDate]
  }

  const handleCalendarChange = (index: number) => (value: PickerValue) => {
    if (!value) {
      return
    }

    const selectedDate = value as Date

    if (calendarOption !== DatePickerOptionValues.Custom_range) {
      setCalendarOption(DatePickerOptionValues.Custom_range)
    }

    if (index === 0) {
      // If user is selecting start date
      setCalendarRange((prev) => calculateNewStartRange(selectedDate, prev))
    } else if (index === 1) {
      // If user is selecting end date
      setCalendarRange((prev) => calculateNewEndRange(selectedDate, prev))
    }
  }

  const handleCalendarOptionSelect = (e: React.MouseEvent<HTMLElement>) => {
    const option = DatePickerOptionValues[e.currentTarget.id as keyof typeof DatePickerOptionValues]
    setCalendarOption(option)
    setCalendarRange(getDateFromOption(option))
  }

  const handleApplyCalendar = () => {
    setSelectedOption(calendarOption)
    setSelectedRange([calendarRange[0], new Date((calendarRange[1] as Date).setHours(23, 59, 59, 999))])
    handleDateChange([calendarRange[0], new Date((calendarRange[1] as Date).setHours(23, 59, 59, 999))], calendarOption)
    handleClose()
  }

  const rangeEnd = selectedRange[1] ?? new Date()
  const dateRangeLabel = `${getLocaleDateString(selectedRange[0])}${
    selectedRange[0]?.setHours(0, 0, 0, 0) === new Date(rangeEnd).setHours(0, 0, 0, 0)
      ? ''
      : ` - ${getLocaleDateString(rangeEnd)}`
  }`

  const label = selectedOption !== DatePickerOptionValues.Custom_range ? _t(selectedOption) : dateRangeLabel

  const input = children?.(label, isOpen) ?? (
    <TextInput
      id="datepicker-input"
      readOnly
      value={label}
      prefix={<i className="ds3-icon ds3-calendar" />}
      postfix={isOpen ? <i className="ds3-icon ds3-chevron-up" /> : <i className="ds3-icon ds3-chevron-down" />}
      cursor="pointer"
    />
  )

  return (
    <OutsideClickHandler onOutsideClick={handleClose}>
      <button
        type="button"
        id="datepicker-container"
        className="unstyled-button datepicker-container w-100"
        onClick={handleToggle}
        ref={referenceElement}
      >
        {input}
        {isOpen && (
          <Popper
            placement={isExpanded ? 'bottom-end' : 'bottom'}
            referenceElement={referenceElement.current!}
            modifiers={[
              {
                name: 'flip',
                options: {
                  fallbackPlacements: ['bottom-end'],
                },
              },
            ]}
          >
            {({ ref, style, placement }) => (
              <div
                ref={ref}
                style={style}
                data-placement={placement}
                className={`dropdown-menu ${isOpen ? 'show' : ''}`}
              >
                {isExpanded ? (
                  <DatePickerCalendar
                    onClose={handleClose}
                    onDateSelect={handleCalendarChange}
                    onOptionSelect={handleCalendarOptionSelect}
                    onApply={handleApplyCalendar}
                    dateRange={calendarRange}
                    selectedOption={calendarOption}
                  />
                ) : (
                  <DatePickerOptions selectedOption={selectedOption} onSelect={handleSimpleOptionSelect} />
                )}
              </div>
            )}
          </Popper>
        )}
      </button>
    </OutsideClickHandler>
  )
}

export default DatePicker

/**
 * @param option    Date picker default option
 * @returns         Date range based on the selected option
 */
const getDateFromOption = (option: DatePickerOptionValues): Range<PickerDate> => {
  // Use common value 'today' to void edge case when 'new Date()' initialize different
  // timestamps when called (e.g at 23:59:59 and another call of 'new Date()' might show
  // the next date at 00:00:00)
  const today = new Date()
  const now = {
    year: today.getFullYear(),
    month: today.getMonth(),
    days: today.getDate(),
    hours: today.getHours(),
    mins: today.getMinutes(),
  }

  switch (option) {
    case DatePickerOptionValues.Last_24_hours:
      return [new Date(now.year, now.month, now.days - 1, now.hours, now.mins), null]
    case DatePickerOptionValues.Last_7_days:
      return [new Date(now.year, now.month, now.days - 7), null]
    case DatePickerOptionValues.Last_30_days:
      return [new Date(now.year, now.month - 1, now.days), null]
    case DatePickerOptionValues.Last_12_months:
      return [new Date(now.year - 1, now.month, now.days), null]
    case DatePickerOptionValues.Custom_range:
      return [today, today] // Start of today
    case DatePickerOptionValues.All_time:
    default:
      return [null, null] // Handled on backend side
  }
}

const getSelectedDateOption = (date_from: Date, date_to: Date | null): DatePickerOptionValues => {
  if (!date_to) {
    const diffInMs = date_from.getTime() - new Date().getTime()
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24))

    if (diffInDays === -1) {
      return DatePickerOptionValues.Last_24_hours
    } else if (diffInDays === -7) {
      return DatePickerOptionValues.Last_7_days
    } else if (diffInDays === -30) {
      return DatePickerOptionValues.Last_30_days
    } else if (diffInDays === -365) {
      return DatePickerOptionValues.Last_12_months
    }
  }

  return DatePickerOptionValues.Custom_range
}
